import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AdminLayout } from '../../components/Layout/AdminLayout';
import { PageHeader } from '../../components/common';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  Tooltip,
} from '@mui/material';
import { Save, Cancel, AutoFixHigh } from '@mui/icons-material';
import { useTouchPoint, useUpdateTouchPoint, useCounties } from '../../hooks';
import { useSnackbar } from '../../contexts/SnackbarContext';
import { generateVerifyToken } from '../../utils/tokens';
import { colors } from '../../theme/theme';

export const EditTouchPointPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const updateTouchPoint = useUpdateTouchPoint();
  const { counties } = useCounties();
  const { touchPoint, isLoading } = useTouchPoint(Number(id));

  const [formData, setFormData] = useState({
    touch_point: '',
    status: 'active',
    display_phone_number: '',
    meta_phone_number_id: '',
    meta_waba_id: '',
    meta_access_token: '',
    meta_app_secret: '',
    meta_verify_token: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const isWhatsApp = formData.touch_point === 'whatsapp';

  useEffect(() => {
    if (touchPoint) {
      setFormData({
        touch_point: touchPoint.touch_point || '',
        status: touchPoint.status || 'active',
        display_phone_number: (touchPoint as any).display_phone_number || '',
        meta_phone_number_id: (touchPoint as any).meta_phone_number_id || '',
        meta_waba_id: (touchPoint as any).meta_waba_id || '',
        // Tokens are hidden by the API for security — leave blank and only send if the admin edits them.
        meta_access_token: '',
        meta_app_secret: '',
        meta_verify_token: (touchPoint as any).meta_verify_token || '',
      });
    }
  }, [touchPoint]);

  const handleChange = (field: string, value: string | number) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error when user types
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    try {
      // Don't send blank tokens — that would overwrite existing encrypted values with empty strings.
      const payload: any = { ...formData };
      if (!payload.meta_access_token) delete payload.meta_access_token;
      if (!payload.meta_app_secret) delete payload.meta_app_secret;

      await updateTouchPoint.mutateAsync({
        id: Number(id),
        data: payload,
      });

      showSnackbar('Touch point updated successfully', 'success');
      navigate('/superadmin/touch-points');
    } catch (error: any) {
      const errorData = error.response?.data;
      if (errorData?.errors) {
        setErrors(errorData.errors);
      }
      const errorMessage = errorData?.message || 'Failed to update touch point';
      showSnackbar(errorMessage, 'error');
    }
  };

  if (isLoading) {
    return (
      <AdminLayout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <Box>
        <PageHeader
          title="Edit Touch Point"
          subtitle="Update touch point details"
          breadcrumbs={[
            { label: 'Dashboard', path: '/superadmin/dashboard' },
            { label: 'Touch Points', path: '/superadmin/touch-points' },
            { label: 'Edit' },
          ]}
        />

        <Card
          elevation={0}
          sx={{
            borderRadius: 3,
            border: `1px solid ${colors.grey[200]}`,
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
          }}
        >
          <CardContent sx={{ p: 4 }}>
            {Object.keys(errors).length > 0 && (
              <Alert severity="error" sx={{ mb: 3 }}>
                Please fix the errors below:
                <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
                  {Object.entries(errors).map(([field, messages]) => (
                    <li key={field}>
                      {Array.isArray(messages) ? messages.join(', ') : messages}
                    </li>
                  ))}
                </ul>
              </Alert>
            )}

            <form onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                <Grid size={{ xs: 12 }}>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    <strong>County:</strong> {touchPoint?.county?.name}
                    <br />
                    <strong>Username:</strong> {touchPoint?.username}
                    <br />
                    <strong>Created:</strong> {touchPoint?.created_at ? new Date(touchPoint.created_at).toLocaleDateString() : 'N/A'}
                  </Alert>
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <FormControl fullWidth required error={!!errors.touch_point}>
                    <InputLabel>Touch Point Type</InputLabel>
                    <Select
                      value={formData.touch_point}
                      label="Touch Point Type"
                      onChange={(e) => handleChange('touch_point', e.target.value)}
                    >
                      <MenuItem value="">
                        <em>Select Touch Point Type</em>
                      </MenuItem>
                      <MenuItem value="portal">Citizen Portal</MenuItem>
                      <MenuItem value="mobile">Mobile App</MenuItem>
                      <MenuItem value="whatsapp">WhatsApp</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={formData.status}
                      label="Status"
                      onChange={(e) => handleChange('status', e.target.value)}
                    >
                      <MenuItem value="active">Active</MenuItem>
                      <MenuItem value="inactive">Inactive</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {isWhatsApp && (
                  <>
                    <Grid size={{ xs: 12 }}>
                      <Alert severity="info">
                        WhatsApp credentials. Access token + app secret are stored encrypted and never returned by the API — leave those fields blank to keep the existing values, or enter new ones to rotate.
                      </Alert>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        fullWidth label="Meta Phone Number ID"
                        value={formData.meta_phone_number_id}
                        onChange={(e) => handleChange('meta_phone_number_id', e.target.value)}
                        error={!!errors.meta_phone_number_id}
                        helperText={errors.meta_phone_number_id}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        fullWidth label="Display Phone Number"
                        value={formData.display_phone_number}
                        onChange={(e) => handleChange('display_phone_number', e.target.value)}
                        placeholder="+254700000000"
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        fullWidth label="WhatsApp Business Account ID"
                        value={formData.meta_waba_id}
                        onChange={(e) => handleChange('meta_waba_id', e.target.value)}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        fullWidth label="Webhook Verify Token"
                        value={formData.meta_verify_token}
                        onChange={(e) => handleChange('meta_verify_token', e.target.value)}
                        helperText="Generate a new one to rotate it. Update Meta to match."
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <Tooltip title="Generate random token">
                                <IconButton
                                  size="small" edge="end"
                                  onClick={() => handleChange('meta_verify_token', generateVerifyToken())}
                                >
                                  <AutoFixHigh fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <TextField
                        fullWidth label="Access Token (leave blank to keep current)" multiline minRows={2}
                        value={formData.meta_access_token}
                        onChange={(e) => handleChange('meta_access_token', e.target.value)}
                      />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <TextField
                        fullWidth label="App Secret (leave blank to keep current)" type="password"
                        value={formData.meta_app_secret}
                        onChange={(e) => handleChange('meta_app_secret', e.target.value)}
                      />
                    </Grid>
                  </>
                )}

                <Grid size={{ xs: 12 }}>
                  <Stack direction="row" spacing={2} justifyContent="flex-end">
                    <Button
                      variant="outlined"
                      startIcon={<Cancel />}
                      onClick={() => navigate('/superadmin/touch-points')}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      startIcon={<Save />}
                      disabled={updateTouchPoint.isPending}
                      sx={{
                        background: `linear-gradient(135deg, ${colors.primary.main} 0%, ${colors.primary.dark} 100%)`,
                      }}
                    >
                      {updateTouchPoint.isPending ? 'Updating...' : 'Update Touch Point'}
                    </Button>
                  </Stack>
                </Grid>
              </Grid>
            </form>
          </CardContent>
        </Card>
      </Box>
    </AdminLayout>
  );
};
